import { DeleteOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { type FC, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../components/InHouseTooltip/InHouseTooltip";
import { GlyphButton } from "../../components/UIToolkit/Clickable/GlyphButton";
import { InlineEditableText } from "../../components/UIToolkit/Inputs/InlineEditableText";
import { EyebrowLabel } from "../../components/UIToolkit/molecules/EyebrowLabel";
import { FONT_SIZE, LINE_HEIGHT, NEUTRAL } from "../../theme/tokens";
import { presetNameSx } from "./printoutPresetRowStyles";
import { PRESET_NAME_ERROR_KEYS, PRINTOUT_PRESET_NAME_MAX_LENGTH, type PresetNameError, validatePresetName } from "./printoutPresets";

/**
 * The input's own cap, in UTF-16 units: an emoji takes two, so the 40-character limit (counted the way
 * the server counts, by code point) is checked by `validatePresetName` instead — this only stops a paste
 * from running away.
 */
const NAME_INPUT_MAX_UTF16_UNITS = PRINTOUT_PRESET_NAME_MAX_LENGTH * 2;

type Props = {
	presetId: string;
	name: string;
	/** Every other saved printout's name — a rename can't reuse one, nor Printout 1's label. */
	otherNames: readonly string[];
	printout1Label: string;
	/** Resolves whether the server took the new name. */
	onRename: (name: string) => Promise<boolean>;
	onRemoveRequest: () => void;
};

/**
 * Rename and Remove for the chosen saved printout, under the printout picker. Kept out of the picker
 * itself so the picker stays a plain radio group (a text field inside a radio is not something a screen
 * reader can announce sensibly). The name is `InlineEditableText` — Enter or leaving the field saves,
 * Escape puts the saved name back — and a name that can't be saved is explained as it's typed and isn't sent.
 */
export const PrintoutPresetEditor: FC<Props> = ({ presetId, name, otherNames, printout1Label, onRename, onRemoveRequest }) => {
	const { t } = useTranslation();
	const errorId = useId();
	const [nameError, setNameError] = useState<PresetNameError | null>(null);
	// Bumped to put the field back to the saved name after the server refused a rename.
	const [fieldResetKey, setFieldResetKey] = useState<number>(0);
	/**
	 * The name on its way to the server. `InlineEditableText` commits on Enter and again on the blur that
	 * Enter causes, so without this one rename would be sent twice.
	 */
	const sendingNameRef = useRef<string | null>(null);

	const validate = (draft: string): PresetNameError | null => validatePresetName(draft, otherNames, printout1Label);

	const commit = (next: string): void => {
		const error = validate(next);
		setNameError(error);
		if (error || sendingNameRef.current === next) return;
		sendingNameRef.current = next;
		void onRename(next).then((saved) => {
			sendingNameRef.current = null;
			if (!saved) setFieldResetKey((current) => current + 1);
		});
	};

	return (
		<Box
			data-testid={`printout-preset-editor-${presetId}`}
			sx={{ display: "flex", flexDirection: "column", gap: 0.5, pt: 1, borderTop: `1px solid ${NEUTRAL[200]}` }}
		>
			<EyebrowLabel>{t("printout.preset.name.label")}</EyebrowLabel>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
				<InlineEditableText
					key={fieldResetKey}
					value={name}
					onCommit={commit}
					onDraftChange={(draft) => setNameError(validate(draft))}
					invalid={nameError !== null}
					ariaDescribedBy={nameError ? errorId : undefined}
					ariaLabel={t("printout.preset.name.label")}
					revertOnEscape
					maxLength={NAME_INPUT_MAX_UTF16_UNITS}
					dataTestId={`printout-preset-name-${presetId}`}
					// A resting hairline: here, away from the list, the name has to look like the field it is.
					sx={[presetNameSx(true), { flex: 1, borderColor: NEUTRAL[300] }]}
				/>
				<InHouseTooltip title={t("printout.preset.remove")} placement="top">
					<GlyphButton
						icon={<DeleteOutlined />}
						ariaLabel={t("printout.preset.remove")}
						onClick={onRemoveRequest}
						dataTestId={`printout-preset-remove-${presetId}`}
					/>
				</InHouseTooltip>
			</Box>
			{nameError && (
				<Typography
					id={errorId}
					role="alert"
					data-testid={`printout-preset-name-error-${presetId}`}
					sx={{ fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG, color: "error.main" }}
				>
					{t(PRESET_NAME_ERROR_KEYS[nameError], { max: PRINTOUT_PRESET_NAME_MAX_LENGTH })}
				</Typography>
			)}
		</Box>
	);
};
